import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  MapPinIcon, 
  CalendarIcon, 
  ClockIcon, 
  UserGroupIcon,
  StarIcon,
  CheckIcon,
  XMarkIcon,
  InformationCircleIcon
} from '@heroicons/react/24/outline';
import { StarIcon as StarIconSolid } from '@heroicons/react/24/solid';

const EventSwiper = ({ events, onJoin, onSkip, userJoinedEvents = [] }) => {
  const navigate = useNavigate();
  const [currentIndex, setCurrentIndex] = useState(0);
  const [direction, setDirection] = useState(null);
  const [swiping, setSwiping] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  
  const touchStartX = useRef(0);
  const touchCurrentX = useRef(0);
  const cardRef = useRef(null);

  const currentEvent = events[currentIndex];
  const hasMoreEvents = currentIndex < events.length;

  const handleTouchStart = (e) => {
    touchStartX.current = e.touches[0].clientX;
    setSwiping(true);
  };

  const handleTouchMove = (e) => {
    if (!swiping) return;
    touchCurrentX.current = e.touches[0].clientX;
    const diff = touchCurrentX.current - touchStartX.current;
    
    if (cardRef.current) {
      cardRef.current.style.transform = `translateX(${diff}px) rotate(${diff * 0.05}deg)`;
    }
    
    if (diff > 50) setDirection('right');
    else if (diff < -50) setDirection('left');
    else setDirection(null);
  };

  const handleTouchEnd = () => {
    if (!swiping) return;
    setSwiping(false);
    
    const diff = touchCurrentX.current - touchStartX.current;
    
    if (diff > 100) {
      // Swiped right - Join
      handleJoin();
    } else if (diff < -100) {
      // Swiped left - Skip
      handleSkip();
    } else {
      // Reset position
      if (cardRef.current) {
        cardRef.current.style.transform = 'translateX(0) rotate(0)';
      }
      setDirection(null);
    }
  };

  const handleJoin = () => {
    if (!currentEvent) return;
    
    if (cardRef.current) {
      cardRef.current.style.transform = 'translateX(150%) rotate(30deg)';
    }
    
    setTimeout(() => {
      onJoin(currentEvent);
      setCurrentIndex(prev => prev + 1);
      setDirection(null);
      if (cardRef.current) {
        cardRef.current.style.transform = 'translateX(0) rotate(0)';
      }
    }, 300);
  };

  const handleSkip = () => {
    if (!currentEvent) return;
    
    if (cardRef.current) {
      cardRef.current.style.transform = 'translateX(-150%) rotate(-30deg)';
    }
    
    setTimeout(() => {
      onSkip(currentEvent);
      setCurrentIndex(prev => prev + 1);
      setDirection(null);
      if (cardRef.current) {
        cardRef.current.style.transform = 'translateX(0) rotate(0)';
      }
    }, 300);
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-IN', { 
      weekday: 'short', 
      month: 'short', 
      day: 'numeric' 
    });
  };

  const isJoined = (eventId) => userJoinedEvents.includes(eventId);

  if (!hasMoreEvents) {
    return (
      <div className="flex flex-col items-center justify-center h-full p-8 text-center">
        <div className="w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center mb-4">
          <CheckIcon className="h-12 w-12 text-gray-400" />
        </div>
        <h3 className="text-xl font-bold text-gray-900 mb-2">No More Events</h3>
        <p className="text-gray-600 mb-6">
          You've seen all available events. Check back later for more!
        </p>
        <button
          onClick={() => window.location.reload()}
          className="px-6 py-3 bg-primary-600 text-white rounded-xl font-semibold hover:bg-primary-700 transition"
        >
          Refresh Events
        </button>
      </div>
    );
  }

  return (
    <div className="relative h-full flex flex-col">
      {/* Swipe Instruction */}
      <div className="text-center py-4">
        <p className="text-sm text-gray-500">
          Swipe right to join • Swipe left to skip
        </p>
      </div>

      {/* Card Stack */}
      <div className="flex-1 relative px-4 pb-4">
        {/* Next card (background) */}
        {events[currentIndex + 1] && (
          <div 
            className="absolute inset-x-4 top-0 bg-white rounded-3xl shadow-lg overflow-hidden"
            style={{ transform: 'scale(0.95) translateY(10px)', opacity: 0.5 }}
          >
            <div className="h-64 bg-gray-200" />
          </div>
        )}

        {/* Current Card */}
        <div
          ref={cardRef}
          className="relative bg-white rounded-3xl shadow-2xl overflow-hidden transition-transform duration-200"
          style={{ touchAction: 'none' }}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
        >
          {/* Swipe Indicators */}
          <div className="absolute top-6 left-6 z-10">
            <div className={`px-4 py-2 rounded-full border-4 border-red-500 text-red-500 font-bold text-xl transform -rotate-12 transition-opacity ${
              direction === 'left' ? 'opacity-100' : 'opacity-0'
            }`}>
              SKIP
            </div>
          </div>
          <div className="absolute top-6 right-6 z-10">
            <div className={`px-4 py-2 rounded-full border-4 border-green-500 text-green-500 font-bold text-xl transform rotate-12 transition-opacity ${
              direction === 'right' ? 'opacity-100' : 'opacity-0'
            }`}>
              JOIN
            </div>
          </div>

          {/* Restaurant Image */}
          <div className="h-64 bg-gradient-to-br from-primary-500 to-rose-500 flex items-center justify-center">
            <div className="text-center text-white p-6">
              <h3 className="text-3xl font-bold mb-2">{currentEvent.restaurant?.name}</h3>
              {currentEvent.restaurant?.cuisine && (
                <p className="text-primary-100">{currentEvent.restaurant.cuisine}</p>
              )}
              <div className="flex items-center justify-center mt-4 gap-1">
                <UserGroupIcon className="h-5 w-5 text-white" />
                <span className="font-semibold">Connect Community Event</span>
              </div>
            </div>
          </div>

          {/* Event Details */}
          <div className="p-6">
            {/* Organizer Info */}
            <div className="flex items-center gap-3 mb-4 pb-4 border-b border-gray-100">
              {currentEvent.organizer?.photo ? (
                <img src={currentEvent.organizer.photo} alt="" className="w-12 h-12 rounded-full object-cover" />
              ) : (
                <div className="w-12 h-12 rounded-full bg-gradient-to-br from-primary-500 to-rose-500 flex items-center justify-center text-white font-bold text-lg">
                  {currentEvent.organizer?.name?.charAt(0) || '?'}
                </div>
              )}
              <div>
                <p className="font-semibold text-gray-900">{currentEvent.organizer?.name}</p>
                <p className="text-sm text-gray-500">Organizer</p>
              </div>
              {currentEvent.organizer?.profession && (
                <span className="ml-auto text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded-full">
                  {currentEvent.organizer.profession}
                </span>
              )}
            </div>

            {/* Event Info Grid */}
            <div className="grid grid-cols-2 gap-4 mb-4">
              <div className="flex items-center gap-2">
                <CalendarIcon className="h-5 w-5 text-primary-600" />
                <span className="text-sm text-gray-700">{formatDate(currentEvent.eventDate)}</span>
              </div>
              <div className="flex items-center gap-2">
                <ClockIcon className="h-5 w-5 text-primary-600" />
                <span className="text-sm text-gray-700">{currentEvent.eventTime}</span>
              </div>
              <div className="flex items-center gap-2">
                <UserGroupIcon className="h-5 w-5 text-primary-600" />
                <span className="text-sm text-gray-700">
                  {currentEvent.participants?.length || 0} / {currentEvent.totalSeats} joined
                </span>
              </div>
              <div className="flex items-center gap-2">
                <MapPinIcon className="h-5 w-5 text-primary-600" />
                <span className="text-sm text-gray-700 truncate">
                  {currentEvent.restaurant?.address || 'Nearby'}
                </span>
              </div>
            </div>

            {/* Description */}
            {currentEvent.description && (
              <p className="text-gray-600 text-sm mb-4 line-clamp-2">
                {currentEvent.description}
              </p>
            )}

            {/* Details Toggle */}
            <button
              onClick={() => setShowDetails(!showDetails)}
              className="flex items-center gap-1 text-primary-600 text-sm font-medium mb-4"
            >
              <InformationCircleIcon className="h-4 w-4" />
              {showDetails ? 'Hide Details' : 'Show Details'}
            </button>

            {/* Expanded Details */}
            {showDetails && (
              <div className="bg-gray-50 rounded-xl p-4 mb-4">
                <h4 className="font-semibold text-gray-900 mb-2">Participants</h4>
                <div className="flex flex-wrap gap-2">
                  {currentEvent.participants?.map((p, i) => (
                    <div key={i} className="flex items-center gap-1 bg-white px-2 py-1 rounded-full text-sm">
                      {p.user?.photo ? (
                        <img src={p.user.photo} alt="" className="w-6 h-6 rounded-full object-cover" />
                      ) : (
                        <div className="w-6 h-6 rounded-full bg-primary-100 flex items-center justify-center text-primary-700 font-bold text-xs">
                          {p.user?.name?.charAt(0) || '?'}
                        </div>
                      )}
                      <span className="text-gray-700">{p.user?.name}</span>
                    </div>
                  ))}
                </div>
                
                {currentEvent.requireFemale && (
                  <div className="mt-3 p-2 bg-primary-50 rounded-lg">
                    <p className="text-xs text-primary-700">
                      👩 This event requires at least one female participant
                    </p>
                  </div>
                )}
              </div>
            )}

            {/* Action Buttons (for non-touch devices) */}
            <div className="flex gap-3 mt-4">
              <button
                onClick={handleSkip}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-gray-100 text-gray-700 rounded-xl font-semibold hover:bg-gray-200 transition"
              >
                <XMarkIcon className="h-5 w-5" />
                Skip
              </button>
              <button
                onClick={handleJoin}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-primary-600 text-white rounded-xl font-semibold hover:bg-primary-700 transition"
              >
                <CheckIcon className="h-5 w-5" />
                Join
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Event Counter */}
      <div className="text-center py-4">
        <p className="text-sm text-gray-500">
          {currentIndex + 1} of {events.length} events
        </p>
      </div>
    </div>
  );
};

export default EventSwiper;
